import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@cca/ui';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface Contact {
  readonly id: string;
  readonly type: string;
  readonly company: string | null;
  readonly contact_name: string | null;
  readonly email: string;
  readonly locale: string | null;
  readonly status: string;
  readonly tags: readonly string[] | null;
  readonly accepts_marketing: boolean;
  readonly campaign_eligible: boolean;
  readonly commission_reference: string | null;
  readonly last_communication_at: string | null;
  readonly suppression: { reason: string; created_at: string } | null;
}

interface ContactHistory {
  readonly contact: { id: string; email: string; contact_name: string | null; type: string };
  readonly threads: readonly {
    id: string;
    subject: string | null;
    status: string;
    classification: string | null;
    last_inbound_at: string | null;
  }[];
  readonly campaigns: readonly {
    campaign_id: string;
    state: string;
    sent_at: string | null;
    campaigns: { name: string; state: string } | null;
  }[];
}

const TYPE_TONE: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  CUSTOMER: 'info',
  AFFILIATE: 'success',
  PARTNER: 'warning',
  SUPPLIER: 'neutral',
  OTHER: 'neutral',
};

function HistoryPane({
  contactId,
  accessToken,
}: {
  readonly contactId: string;
  readonly accessToken: string | null;
}) {
  const history = useQuery({
    queryKey: ['contact-history', contactId],
    enabled: Boolean(accessToken),
    queryFn: () =>
      apiRequest<ContactHistory>(`/api/contacts/${contactId}/history`, { accessToken }),
  });

  if (history.isPending) return <LoadingState label="Loading history" />;
  if (history.isError) {
    return (
      <ErrorState
        description={
          history.error instanceof ApiError ? history.error.message : 'Could not load history.'
        }
        onRetry={() => void history.refetch()}
      />
    );
  }

  const { contact, threads, campaigns } = history.data;

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {contact.contact_name ?? contact.email}
        </h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {contact.type.toLowerCase()} · {contact.email}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Conversations</CardTitle>
          <CardDescription>Email threads associated with this contact.</CardDescription>
        </CardHeader>
        <CardContent>
          {threads.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">No conversations yet.</p>
          ) : (
            <ul className="space-y-2">
              {threads.map((thread) => (
                <li key={thread.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                  <Badge tone="neutral">{thread.status.toLowerCase()}</Badge>
                  <span className="text-slate-800 dark:text-slate-200">
                    {thread.subject ?? '(no subject)'}
                  </span>
                  {thread.classification ? (
                    <span className="text-slate-500 dark:text-slate-400">
                      {thread.classification.replaceAll('_', ' ').toLowerCase()}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>
            What this address actually received, from the snapshot taken at approval.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Never included in a campaign.
            </p>
          ) : (
            <ul className="space-y-2">
              {campaigns.map((row) => (
                <li key={row.campaign_id} className="flex flex-wrap items-baseline gap-2 text-xs">
                  <Badge
                    tone={
                      row.state === 'SENT'
                        ? 'success'
                        : row.state === 'FAILED'
                          ? 'danger'
                          : 'neutral'
                    }
                  >
                    {row.state.toLowerCase().replaceAll('_', ' ')}
                  </Badge>
                  <span className="text-slate-800 dark:text-slate-200">
                    {row.campaigns?.name ?? 'campaign'}
                  </span>
                  {row.sent_at ? (
                    <span className="text-slate-400">
                      {new Date(row.sent_at).toLocaleDateString()}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContactsPage() {
  const accessToken = useAccessToken();
  const [type, setType] = useState<'ALL' | 'AFFILIATE' | 'PARTNER' | 'SUPPLIER' | 'CUSTOMER'>(
    'ALL',
  );
  const [selected, setSelected] = useState<string | null>(null);

  const contacts = useQuery({
    queryKey: ['contacts', type],
    enabled: Boolean(accessToken),
    queryFn: () =>
      apiRequest<{ contacts: Contact[] }>(
        `/api/contacts?limit=200${type === 'ALL' ? '' : `&type=${type}`}`,
        { accessToken },
      ),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Contacts</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Affiliates, partners and suppliers, with their consent state and what they have received.
        </p>
      </header>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by type">
        {(['ALL', 'AFFILIATE', 'PARTNER', 'SUPPLIER', 'CUSTOMER'] as const).map((option) => (
          <Button
            key={option}
            size="sm"
            variant={type === option ? 'primary' : 'outline'}
            aria-pressed={type === option}
            onClick={() => setType(option)}
          >
            {option}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <Card className="overflow-hidden p-0">
          {contacts.isPending ? <LoadingState label="Loading contacts" /> : null}

          {contacts.isError ? (
            <ErrorState
              description={
                contacts.error instanceof ApiError ? contacts.error.message : 'Could not load.'
              }
              onRetry={() => void contacts.refetch()}
            />
          ) : null}

          {contacts.data && contacts.data.contacts.length === 0 ? (
            <EmptyState
              title="No contacts"
              description="Affiliates, partners and suppliers appear here once added. `pnpm demo:seed` creates one affiliate."
            />
          ) : null}

          <ul>
            {(contacts.data?.contacts ?? []).map((contact) => (
              <li key={contact.id}>
                <button
                  type="button"
                  onClick={() => setSelected(contact.id)}
                  aria-current={selected === contact.id ? 'true' : undefined}
                  className={`w-full border-b border-slate-200 px-3 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-slate-800 ${
                    selected === contact.id
                      ? 'bg-sky-50 dark:bg-sky-950'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-900'
                  }`}
                >
                  <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                    {contact.contact_name ?? contact.company ?? contact.email}
                  </span>
                  <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                    {contact.email}
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-1">
                    <Badge tone={TYPE_TONE[contact.type] ?? 'neutral'}>
                      {contact.type.toLowerCase()}
                    </Badge>
                    {/* Consent and suppression are shown side by side because
                        they answer different questions: may we market to them,
                        and may we email them at all. */}
                    <Badge tone={contact.accepts_marketing ? 'success' : 'neutral'}>
                      {contact.accepts_marketing ? 'marketing ok' : 'no marketing'}
                    </Badge>
                    {contact.suppression ? (
                      <Badge tone="danger">
                        {contact.suppression.reason.toLowerCase().replaceAll('_', ' ')}
                      </Badge>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <div>
          {selected ? (
            <HistoryPane contactId={selected} accessToken={accessToken} />
          ) : (
            <Card>
              <EmptyState
                title="Select a contact"
                description="Choose one on the left to see their conversations and the campaigns they actually received."
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export const contactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts',
  component: () => (
    <RequireSession>
      <ContactsPage />
    </RequireSession>
  ),
});
